import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import {
  creditLine,
  emptyAddress,
  operations,
  policies,
  policyholderCcg,
  policyholderComplement,
  policyholderFile,
  products,
  type Address,
  type CCGParty,
  type CCGPartyInput,
  type CCGRole,
  type CommercialTerm,
  type CreditConsultation,
  type CreditLimitGroup,
  type Partner,
  type PartnerRole,
  type Policy,
  type Policyholder,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatCpfCnpj,
  formatDocumentoDeTerceiro,
  formatDate,
  formatDateTime,
  formatMoney,
  formatRate,
  moneyInputError,
  moneyToInput,
  parseMoney,
  parseRate,
  rateInputError,
  rateToInput,
  toMoney,
  zeroMoney,
} from '@/shared/lib/format';
import { nomeDoProvedor } from '@/shared/lib/nomeDoProvedor';
import { AddressFields } from '@/shared/ui/AddressFields';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { HistoryTab } from '@/shared/ui/HistoryTab';
import { Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { DocumentsPanel } from '../../documents/DocumentsPanel';
import { QuoteBilletsDialog } from '../../operations/quote/QuoteBilletsDialog';
import { BilletsDialog } from '../../portfolio/BilletsDialog';
import { temDocumentoDaApolice } from '../../portfolio/documentoDaApolice';
import { fieldErrorOf } from '../../shared/fieldError';
import {
  DICA_DO_GRUPO_INDISPONIVEL,
  gruposDeModalidade,
  PENDENCIA_ANALISES_CORRETORA,
  PENDENCIA_ASSINATURA_SEGURADORA,
  PENDENCIA_LIMITE_DO_MOTOR,
  rotuloDaAcao,
  tiposDeCondicao,
} from '../rotulos';
import { ContratoDaCcg } from './ContratoDaCcg';
import {
  HistoricoDeConsumo,
  LimiteCalculadoDoTomador,
  OCULTO_AO_CORRETOR,
} from './LimiteCalculado';
import { HistoricoDeLimites } from './RegrasEHistorico';
import { AcaoIndisponivel, Bloco, Secao, TabelaDeHistorico } from './shared';
import { tipoDaConsulta } from './valorDaConsulta';
import { DadosDaConsultaModal } from './DadosDaConsultaModal';

function useInvalidar(id: string) {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ['policyholder', id] });
}

function erroDaApi(erro: Error | null): ApiError | null {
  return erro instanceof ApiError ? erro : null;
}

function enderecoEmLinha(endereco: Address): string {
  return [
    endereco.street,
    endereco.number,
    endereco.complement,
    endereco.district,
    endereco.city,
    endereco.state,
    endereco.zip_code,
  ]
    .filter((parte) => parte && parte.trim() !== '')
    .join(', ');
}

function percentual(bps: number | null | undefined): string {
  return bps === null || bps === undefined ? '-' : formatRate(bps);
}

// --- Filiais ---------------------------------------------------------------------

export function Filiais({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const toast = useToast();
  const invalidar = useInvalidar(tomador.id);
  const podeIncluir = can('tomadores.tomador.update');
  const [incluindo, setIncluindo] = useState(false);
  const [cnpj, setCnpj] = useState('');
  const [razao, setRazao] = useState('');
  const [endereco, setEndereco] = useState<Address>(emptyAddress);

  const filiais = useQuery({
    queryKey: ['policyholder', tomador.id, 'branches'],
    queryFn: ({ signal }) => policyholderFile.branches(tomador.id, signal),
  });
  const incluir = useMutation({
    mutationFn: () =>
      policyholderFile.addBranch(tomador.id, {
        document: cnpj,
        legal_name: razao,
        address: endereco,
      }),
    onSuccess: () => {
      toast.success('Filial incluida.');
      setIncluindo(false);
      setCnpj('');
      setRazao('');
      setEndereco(emptyAddress);
      invalidar();
    },
  });
  const remover = useMutation({
    mutationFn: (filialId: string) =>
      policyholderFile.removeBranch(tomador.id, filialId),
    onSuccess: () => {
      toast.success('Filial removida.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const apiError = erroDaApi(incluir.error);

  return (
    <Secao
      titulo="Filiais"
      descricao="Estabelecimentos do tomador, com a mesma raiz de CNPJ."
      carregando={filiais.isPending}
      erro={filiais.error}
      acao={
        podeIncluir ? (
          <Button size="sm" onClick={() => setIncluindo(true)}>
            Adicionar filial
          </Button>
        ) : null
      }
    >
      <DataTable
        columns={[
          { key: 'cnpj', header: 'CNPJ', render: (f) => formatCpfCnpj(f.document) },
          { key: 'razao', header: 'Razao Social', render: (f) => f.legal_name },
          {
            key: 'endereco',
            header: 'Endereco',
            render: (f) => enderecoEmLinha(f.address) || '-',
          },
          {
            key: 'acoes',
            header: 'Acoes',
            render: (f) =>
              podeIncluir ? (
                <Button size="sm" variant="danger" onClick={() => remover.mutate(f.id)}>
                  Remover
                </Button>
              ) : null,
          },
        ]}
        items={filiais.data ?? []}
        keyOf={(f) => f.id}
        empty={<EmptyState icon="briefcase" title="Nenhuma filial cadastrada" />}
      />
      <Modal
        open={incluindo}
        onClose={() => setIncluindo(false)}
        title="Adicionar nova filial"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIncluindo(false)}>
              Cancelar
            </Button>
            <Button loading={incluir.isPending} onClick={() => incluir.mutate()}>
              Salvar filial
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {incluir.error ? (
            <Alert tone="danger" title="Nao foi possivel incluir">
              {incluir.error.message}
            </Alert>
          ) : null}
          <p className="text-2xs text-ink-500">
            Sem consulta a Receita, razao social e endereco da filial sao digitados.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Digite o CNPJ da filial"
              required
              placeholder="00.000.000/0000-00"
              value={cnpj}
              error={fieldErrorOf(apiError, 'document')}
              onChange={(event) => setCnpj(event.target.value)}
            />
            <Field
              label="Razao Social da filial"
              required
              value={razao}
              error={fieldErrorOf(apiError, 'legal_name')}
              onChange={(event) => setRazao(event.target.value)}
            />
          </div>
          <AddressFields value={endereco} onChange={setEndereco} />
        </div>
      </Modal>
    </Secao>
  );
}

// --- Grupo Economico ---------------------------------------------------------------
// A secao passou ao grupo economico como entidade (P-TOM-C5): GrupoENomeacao.tsx.

// --- Limites e Taxas ---------------------------------------------------------------

/** Quem aprovou o limite do grupo, e quando. A corretora nao recebe esse dado. */
function OrigemDoLimite({ limite }: { limite: CreditLimitGroup }) {
  if (!limite.approved_origin) return <span className="text-ink-400">-</span>;
  const quando = limite.approved_at ? formatDateTime(limite.approved_at) : '';
  return (
    <span className="flex flex-col">
      <span>
        {limite.approved_origin === 'motor'
          ? 'Automatico (Motor de credito)'
          : 'Manual'}
      </span>
      <span className="text-2xs text-ink-500">
        {limite.approved_origin === 'motor'
          ? quando
          : [limite.approved_label, quando].filter(Boolean).join(', ')}
      </span>
    </span>
  );
}

export function LimitesETaxas({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const toast = useToast();
  const invalidar = useInvalidar(tomador.id);
  const podeEditar = can('tomadores.credito.update');
  const [aba, setAba] = useState<'atual' | 'consumo' | 'historico'>('atual');
  const [editando, setEditando] = useState<CreditLimitGroup | null>(null);
  const [aprovado, setAprovado] = useState('');
  const [utilizado, setUtilizado] = useState('');
  const [publica, setPublica] = useState('');
  const [privada, setPrivada] = useState('');
  const [validade, setValidade] = useState('');
  const [motivo, setMotivo] = useState('');

  const limites = useQuery({
    queryKey: ['policyholder', tomador.id, 'credit-limits'],
    queryFn: ({ signal }) => policyholderFile.creditLimits(tomador.id, signal),
  });
  // A validade vem da regra da empresa (LIM-Q08), calculada no servidor: a mesma leitura
  // do limite calculado, abaixo.
  const calculado = useQuery({
    queryKey: ['policyholder', tomador.id, 'credit-line'],
    queryFn: ({ signal }) => creditLine.ofPolicyholder(tomador.id, signal),
    enabled: aba === 'atual',
  });
  function validadeDoGrupo(limite: CreditLimitGroup): string {
    const dimensao = calculado.data?.lines
      .find((linha) => linha.modality_group === limite.modality_group)
      ?.dimensions.find((d) => d.dimension === 'grupo_de_modalidade');
    const data = dimensao?.valid_until?.slice(0, 10) ?? limite.valid_until;
    if (!data) return '-';
    return dimensao?.validity === 'vencido'
      ? `${formatCivilDate(data)} (vencido)`
      : formatCivilDate(data);
  }
  const temLimiteDoMotor = (limites.data ?? []).some((l) => l.engine_limit !== null);
  // Quem aprovou nao vai a corretora: sem nenhuma origem, a coluna nao aparece.
  const temOrigem = (limites.data ?? []).some((l) => Boolean(l.approved_origin));
  // PM-09: com cartao de credito ou de PEP aberto, a corretora ve os grupos sem valores.
  const oculto = (limites.data ?? []).some((l) => l.hidden_from_broker === true);
  const valorOuOculto = (l: CreditLimitGroup, valor: string) =>
    l.hidden_from_broker ? 'oculto' : valor;
  const historico = useQuery({
    queryKey: ['policyholder', tomador.id, 'limit-history'],
    queryFn: ({ signal }) => policyholderFile.limitHistory(tomador.id, signal),
    enabled: aba === 'historico',
  });
  // "Informacao do grupo" do OnPoint (TOM-122): a descricao dos grupos de modalidades
  // do grupo de credito, ou as modalidades da empresa que estao nele quando nenhum
  // grupo tem descricao cadastrada.
  const gruposDeCredito = useQuery({
    queryKey: ['policyholders', 'credit-groups'],
    queryFn: ({ signal }) => policyholderComplement.creditGroups(signal),
    staleTime: 5 * 60_000,
  });
  function dicaDoGrupo(grupo: string): string {
    const info = gruposDeCredito.data?.find((item) => item.modality_group === grupo);
    if (!info) return DICA_DO_GRUPO_INDISPONIVEL;
    if (info.description) return info.description;
    return info.modalities.length > 0
      ? `Modalidades da empresa neste grupo: ${info.modalities.join(', ')}.`
      : 'Nenhuma modalidade da empresa esta neste grupo de credito.';
  }

  function abrir(limite: CreditLimitGroup) {
    setEditando(limite);
    setAprovado(moneyToInput(limite.approved));
    setUtilizado(moneyToInput(limite.used));
    setPublica(
      limite.public_rate_bps === null ? '' : rateToInput(limite.public_rate_bps),
    );
    setPrivada(
      limite.private_rate_bps === null ? '' : rateToInput(limite.private_rate_bps),
    );
    setValidade(limite.valid_until ?? '');
    setMotivo('');
  }

  const salvar = useMutation({
    mutationFn: () => {
      if (!editando) throw new Error('Nenhum grupo em edicao.');
      return policyholderFile.saveCreditLimit(tomador.id, editando.modality_group, {
        approved: aprovado ? toMoney(aprovado) : zeroMoney(),
        used: utilizado ? toMoney(utilizado) : zeroMoney(),
        public_rate_bps: publica.trim() === '' ? null : parseRate(publica),
        private_rate_bps: privada.trim() === '' ? null : parseRate(privada),
        valid_until: validade.trim() === '' ? null : validade,
        reason: motivo.trim(),
      });
    },
    onSuccess: () => {
      toast.success('Limite e taxa salvos.');
      setEditando(null);
      invalidar();
    },
  });
  // O motivo acompanha a mudanca do aprovado. Mudar so a taxa ou o utilizado nao pede.
  const mudouOAprovado =
    editando !== null &&
    (parseMoney(aprovado)?.amount ?? 0) !== editando.approved.amount;
  const invalido =
    moneyInputError(aprovado) !== undefined ||
    moneyInputError(utilizado) !== undefined ||
    rateInputError(publica) !== undefined ||
    rateInputError(privada) !== undefined ||
    (mudouOAprovado && motivo.trim() === '');

  return (
    <Secao
      titulo="Limites e Taxas de Credito do Tomador"
      descricao="Grupos de modalidades individuais: os limites sao controlados separadamente."
      carregando={limites.isPending}
      erro={limites.error}
    >
      <div
        role="tablist"
        aria-label="Limites e Taxas"
        className="flex gap-1 border-b border-line"
      >
        {(
          [
            ['atual', 'Configuracao atual'],
            ['consumo', 'Consumo e liberacao'],
            ['historico', 'Historico'],
          ] as const
        ).map(([chave, titulo]) => (
          <button
            key={chave}
            type="button"
            role="tab"
            aria-selected={aba === chave}
            onClick={() => setAba(chave)}
            className={
              aba === chave
                ? '-mb-px border-b-2 border-brand-500 px-3 py-2 text-xs font-semibold text-brand-700'
                : '-mb-px border-b-2 border-transparent px-3 py-2 text-xs font-semibold text-ink-500'
            }
          >
            {titulo}
          </button>
        ))}
      </div>
      {aba === 'atual' ? (
        <>
          {oculto ? <Alert tone="info">{OCULTO_AO_CORRETOR}</Alert> : null}
          <DataTable
            columns={[
              {
                key: 'grupo',
                header: 'Grupo de modalidades',
                render: (l) => (
                  <span>
                    {gruposDeModalidade[l.modality_group] ?? l.modality_group}
                    <span
                      role="img"
                      tabIndex={0}
                      aria-label={`Informacao do grupo: ${dicaDoGrupo(l.modality_group)}`}
                      title={dicaDoGrupo(l.modality_group)}
                      className="ml-1 cursor-help text-2xs text-brand-600"
                    >
                      (i)
                    </span>
                    {!l.configured ? (
                      <span className="ml-2 text-2xs text-ink-400">
                        sem limite gravado
                      </span>
                    ) : null}
                  </span>
                ),
              },
              {
                key: 'aprovado',
                header: 'Aprovado',
                align: 'right',
                render: (l) => valorOuOculto(l, formatMoney(l.approved)),
              },
              {
                key: 'utilizado',
                header: 'Utilizado informado',
                align: 'right',
                render: (l) => valorOuOculto(l, formatMoney(l.used)),
              },
              {
                key: 'disponivel',
                header: 'Disponivel (aprovado menos informado)',
                align: 'right',
                render: (l) => valorOuOculto(l, formatMoney(l.available)),
              },
              {
                key: 'validade',
                header: 'Validade',
                render: (l) => valorOuOculto(l, validadeDoGrupo(l)),
              },
              ...(temOrigem
                ? [
                    {
                      key: 'origem',
                      header: 'Origem',
                      render: (l: CreditLimitGroup) => <OrigemDoLimite limite={l} />,
                    },
                  ]
                : []),
              {
                key: 'taxa',
                header: 'Taxa Seguro Publico/Privado',
                render: (l) =>
                  valorOuOculto(
                    l,
                    `${percentual(l.public_rate_bps)} / ${percentual(l.private_rate_bps)}`,
                  ),
              },
              // O limite que o motor calculou no reprocessamento (P-TOM-C2), so para
              // quem o ve: a coluna aparece quando ha valor.
              ...(temLimiteDoMotor
                ? [
                    {
                      key: 'motor',
                      header: 'Limite do motor',
                      align: 'right' as const,
                      render: (l: CreditLimitGroup) =>
                        l.engine_limit ? (
                          <span title={PENDENCIA_LIMITE_DO_MOTOR}>
                            {formatMoney(l.engine_limit)}
                          </span>
                        ) : (
                          '-'
                        ),
                    },
                  ]
                : []),
              {
                key: 'acoes',
                header: 'Acoes',
                render: (l) =>
                  podeEditar ? (
                    <Button size="sm" variant="secondary" onClick={() => abrir(l)}>
                      Editar
                    </Button>
                  ) : null,
              },
            ]}
            items={limites.data ?? []}
            keyOf={(l) => l.modality_group}
          />
          <p className="text-2xs text-ink-500">
            Esta tabela e o limite gravado: o aprovado, o utilizado informado a mao e as
            taxas. O utilizado calculado pela metrica da empresa, o reservado e o
            disponivel por dimensao estao no limite calculado, abaixo.
          </p>
          <LimiteCalculadoDoTomador tomadorId={tomador.id} />
        </>
      ) : aba === 'consumo' ? (
        <HistoricoDeConsumo tomadorId={tomador.id} />
      ) : (
        <HistoricoDeLimites eventos={historico.data ?? []} />
      )}
      <Modal
        open={editando !== null}
        onClose={() => setEditando(null)}
        title={`Limite do grupo ${editando ? (gruposDeModalidade[editando.modality_group] ?? '') : ''}`}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button
              loading={salvar.isPending}
              disabled={invalido}
              onClick={() => salvar.mutate()}
            >
              Salvar
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {salvar.error ? (
            <div className="sm:col-span-2">
              <Alert tone="danger" title="Nao foi possivel salvar">
                {salvar.error.message}
              </Alert>
            </div>
          ) : null}
          <MoneyField
            label="Aprovado (R$)"
            value={aprovado}
            error={moneyInputError(aprovado)}
            onValueChange={setAprovado}
          />
          <MoneyField
            label="Utilizado (R$)"
            value={utilizado}
            error={moneyInputError(utilizado)}
            onValueChange={setUtilizado}
          />
          <Field
            label="Taxa seguro publico (%)"
            inputMode="decimal"
            value={publica}
            error={rateInputError(publica)}
            onChange={(event) => setPublica(event.target.value)}
          />
          <Field
            label="Taxa seguro privado (%)"
            inputMode="decimal"
            value={privada}
            error={rateInputError(privada)}
            onChange={(event) => setPrivada(event.target.value)}
          />
          <Field
            label="Validade do limite"
            type="date"
            value={validade}
            hint="Data ate a qual o limite aprovado vale. A regra de validade da empresa diz se ela conta (LIM-Q08)."
            onChange={(event) => setValidade(event.target.value)}
          />
          <div className="sm:col-span-2">
            <Textarea
              label="Motivo"
              value={motivo}
              maxLength={2000}
              required={mudouOAprovado}
              hint={
                mudouOAprovado
                  ? 'Obrigatorio para mudar o limite aprovado. Fica no historico e na trilha.'
                  : 'Pedido quando o limite aprovado muda.'
              }
              onChange={(event) => setMotivo(event.target.value)}
            />
          </div>
        </div>
      </Modal>
    </Secao>
  );
}

// --- Condicoes Comerciais -------------------------------------------------------------

export function CondicoesComerciais({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const toast = useToast();
  const invalidar = useInvalidar(tomador.id);
  const podeIncluir = can('tomadores.condicao.update');
  const [incluindo, setIncluindo] = useState(false);
  const [modalidade, setModalidade] = useState('');
  const [fixo, setFixo] = useState('');
  const [minimo, setMinimo] = useState('');
  // Edicao da linha (TOM-931): o tipo e o valor; a modalidade nao muda.
  const [editando, setEditando] = useState<CommercialTerm | null>(null);
  const [tipoEditado, setTipoEditado] = useState<CommercialTerm['kind']>('premio_fixo');
  const [valorEditado, setValorEditado] = useState('');

  const condicoes = useQuery({
    queryKey: ['policyholder', tomador.id, 'commercial-terms'],
    queryFn: ({ signal }) => policyholderFile.commercialTerms(tomador.id, signal),
  });
  const catalogo = useQuery({
    queryKey: ['products'],
    queryFn: ({ signal }) => products.list(signal),
    enabled: can('produtos.produto.view'),
  });
  const modalidades = (catalogo.data ?? []).flatMap((produto) =>
    produto.modalities.map((item) => ({
      id: item.id,
      nome: `${item.name} (${produto.name})`,
    })),
  );
  const nomeDaModalidade = new Map(modalidades.map((item) => [item.id, item.nome]));

  const incluir = useMutation({
    mutationFn: () =>
      policyholderFile.addCommercialTerms(tomador.id, {
        modality_id: modalidade,
        ...(fixo ? { fixed_premium: toMoney(fixo) } : {}),
        ...(minimo ? { minimum_premium: toMoney(minimo) } : {}),
      }),
    onSuccess: () => {
      toast.success('Condicao incluida.');
      setIncluindo(false);
      setModalidade('');
      setFixo('');
      setMinimo('');
      invalidar();
    },
  });
  const remover = useMutation({
    mutationFn: (termoId: string) =>
      policyholderFile.removeCommercialTerm(tomador.id, termoId),
    onSuccess: () => {
      toast.success('Condicao removida.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const editar = useMutation({
    mutationFn: (termo: CommercialTerm) =>
      policyholderFile.updateCommercialTerm(tomador.id, termo.id, {
        ...(tipoEditado !== termo.kind ? { kind: tipoEditado } : {}),
        amount: toMoney(valorEditado),
      }),
    onSuccess: () => {
      toast.success('Condicao alterada.');
      setEditando(null);
      invalidar();
    },
  });
  const abrirEdicao = (termo: CommercialTerm) => {
    editar.reset();
    setTipoEditado(termo.kind);
    setValorEditado(moneyToInput(termo.amount));
    setEditando(termo);
  };
  const apiError = erroDaApi(incluir.error);
  const erroDaEdicao = erroDaApi(editar.error);

  return (
    <Secao
      titulo="Condicoes Comerciais"
      descricao="Premio fixo ou minimo combinado com o tomador por modalidade. O calculo do premio ainda nao os aplica (regra pendente)."
      carregando={condicoes.isPending}
      erro={condicoes.error}
      acao={
        podeIncluir ? (
          <Button size="sm" onClick={() => setIncluindo(true)}>
            Adicionar condicao
          </Button>
        ) : null
      }
    >
      <DataTable
        columns={[
          {
            key: 'modalidade',
            header: 'Modalidade',
            render: (t) => nomeDaModalidade.get(t.modality_id) ?? t.modality_id,
          },
          {
            key: 'tipo',
            header: 'Tipo',
            render: (t) => tiposDeCondicao[t.kind] ?? t.kind,
          },
          {
            key: 'valor',
            header: 'Valor',
            align: 'right',
            render: (t) => formatMoney(t.amount),
          },
          {
            key: 'acoes',
            header: 'Acoes',
            render: (t) =>
              podeIncluir ? (
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => abrirEdicao(t)}>
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => remover.mutate(t.id)}
                  >
                    Remover
                  </Button>
                </div>
              ) : null,
          },
        ]}
        items={condicoes.data ?? []}
        keyOf={(t) => t.id}
        empty={<EmptyState icon="coins" title="Nenhuma condicao comercial" />}
      />
      <Modal
        open={editando !== null}
        onClose={() => setEditando(null)}
        title="Editar condicao"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditando(null)}>
              Fechar
            </Button>
            <Button
              loading={editar.isPending}
              disabled={
                valorEditado === '' || moneyInputError(valorEditado) !== undefined
              }
              onClick={() => editando && editar.mutate(editando)}
            >
              Salvar
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {editar.error ? (
            <Alert tone="danger" title="Nao foi possivel alterar">
              {editar.error.message}
            </Alert>
          ) : null}
          <p className="text-sm text-ink-600">
            Modalidade:{' '}
            {editando
              ? (nomeDaModalidade.get(editando.modality_id) ?? editando.modality_id)
              : ''}
            . Para outra modalidade, inclua uma condicao nova.
          </p>
          <Select
            label="Tipo"
            options={Object.entries(tiposDeCondicao).map(([value, label]) => ({
              value,
              label,
            }))}
            value={tipoEditado}
            error={fieldErrorOf(erroDaEdicao, 'kind')}
            onChange={(event) =>
              setTipoEditado(event.target.value as CommercialTerm['kind'])
            }
          />
          <MoneyField
            label="Valor (R$)"
            value={valorEditado}
            error={
              moneyInputError(valorEditado) ?? fieldErrorOf(erroDaEdicao, 'amount')
            }
            onValueChange={setValorEditado}
          />
          {editando?.updated_at ? (
            <p className="text-xs text-ink-400">
              Ultima alteracao por {editando.updated_label || 'sistema'} em{' '}
              {formatDateTime(editando.updated_at)}.
            </p>
          ) : null}
        </div>
      </Modal>
      <Modal
        open={incluindo}
        onClose={() => setIncluindo(false)}
        title="Adicionar condicao"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIncluindo(false)}>
              Fechar
            </Button>
            <Button
              loading={incluir.isPending}
              disabled={
                modalidade === '' ||
                (fixo === '' && minimo === '') ||
                moneyInputError(fixo) !== undefined ||
                moneyInputError(minimo) !== undefined
              }
              onClick={() => incluir.mutate()}
            >
              Confirmar
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {incluir.error ? (
            <Alert tone="danger" title="Nao foi possivel incluir">
              {incluir.error.message}
            </Alert>
          ) : null}
          <Select
            label="Modalidade"
            placeholder="Escolha a modalidade"
            options={modalidades.map((item) => ({ value: item.id, label: item.nome }))}
            value={modalidade}
            error={fieldErrorOf(apiError, 'modality_id')}
            onChange={(event) => setModalidade(event.target.value)}
          />
          <MoneyField
            label="Premio fixo (R$)"
            value={fixo}
            error={moneyInputError(fixo)}
            onValueChange={setFixo}
          />
          <MoneyField
            label="Premio Minimo (R$)"
            value={minimo}
            error={moneyInputError(minimo)}
            onValueChange={setMinimo}
          />
        </div>
      </Modal>
    </Secao>
  );
}

// --- CCG ---------------------------------------------------------------------

const vazioDaParte = (papel: CCGRole): CCGPartyInput => ({
  role: papel,
  document: '',
  name: '',
  email: '',
  phone: '',
  nationality: '',
  marital_status: '',
  profession: '',
  identity_document: '',
  address: emptyAddress,
  representative_document: '',
  representative_name: '',
  representative_email: '',
  spouse_name: '',
  spouse_document: '',
});

export function Ccg({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const toast = useToast();
  const invalidar = useInvalidar(tomador.id);
  const podeEditar = can('tomadores.ficha.update');
  const [parte, setParte] = useState<CCGPartyInput | null>(null);
  const [historicoAberto, setHistoricoAberto] = useState(false);

  const partes = useQuery({
    queryKey: ['policyholder', tomador.id, 'ccg-parties'],
    queryFn: ({ signal }) => policyholderFile.ccgParties(tomador.id, signal),
  });
  // A mesma consulta do contrato (ContratoDaCcg): com o contrato em assinatura ou
  // assinado, o servidor recusa incluir ou tirar parte, e a tela diz por que antes
  // (achado R5-B10).
  const contrato = useQuery({
    queryKey: ['policyholder', tomador.id, 'ccg-contract'],
    queryFn: ({ signal }) => policyholderCcg.get(tomador.id, signal),
  });
  const situacaoDoContrato = contrato.data?.contract?.status;
  const partesTravadas =
    situacaoDoContrato === 'em_assinatura' || situacaoDoContrato === 'assinado';
  const motivoDaTrava = partesTravadas
    ? `O contrato de CCG do tomador esta ${
        situacaoDoContrato === 'assinado' ? 'assinado' : 'em assinatura'
      }: as partes nao mudam. Cancele o contrato, ou volte-o para rascunho se o pedido terminou sem assinatura.`
    : '';
  const historico = useQuery({
    queryKey: ['policyholder', tomador.id, 'change-history'],
    queryFn: ({ signal }) => policyholderFile.changeHistory(tomador.id, signal),
    enabled: historicoAberto,
  });
  const incluir = useMutation({
    mutationFn: () => {
      if (!parte) throw new Error('Nenhuma parte em edicao.');
      return policyholderFile.addCcgParty(tomador.id, parte);
    },
    onSuccess: () => {
      toast.success('Parte incluida na CCG.');
      setParte(null);
      invalidar();
    },
  });
  const remover = useMutation({
    mutationFn: (parteId: string) =>
      policyholderFile.removeCcgParty(tomador.id, parteId),
    onSuccess: () => {
      toast.success('Parte removida da CCG.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const apiError = erroDaApi(incluir.error);

  function tabela(papel: CCGRole, titulo: string) {
    const itens = (partes.data ?? []).filter((item) => item.role === papel);
    return (
      <Bloco titulo={titulo}>
        <DataTable
          columns={[
            { key: 'nome', header: 'Nome', render: (p: CCGParty) => p.name || '-' },
            {
              key: 'documento',
              header: 'CPF/CNPJ',
              render: (p: CCGParty) => formatCpfCnpj(p.document),
            },
            { key: 'email', header: 'E-mail', render: (p: CCGParty) => p.email || '-' },
            ...(papel === 'fiador_pj'
              ? [
                  {
                    key: 'representante',
                    header: 'Representante',
                    render: (p: CCGParty) =>
                      `${p.representative_name || ''} ${formatCpfCnpj(p.representative_document)}`.trim(),
                  },
                ]
              : []),
            {
              key: 'acoes',
              header: 'Acoes',
              render: (p: CCGParty) =>
                podeEditar ? (
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={partesTravadas}
                    title={partesTravadas ? motivoDaTrava : undefined}
                    onClick={() => remover.mutate(p.id)}
                  >
                    Remover
                  </Button>
                ) : null,
            },
          ]}
          items={itens}
          keyOf={(p) => p.id}
          empty={<p className="text-sm text-ink-400">Nenhuma pessoa cadastrada.</p>}
        />
      </Bloco>
    );
  }

  function set<K extends keyof CCGPartyInput>(campo: K, valor: CCGPartyInput[K]) {
    setParte((atual) => (atual ? { ...atual, [campo]: valor } : atual));
  }

  const linhasDaCcg = (historico.data ?? []).filter((linha) =>
    linha.action.startsWith('tomadores.ccg'),
  );

  return (
    <Secao
      titulo="CCG"
      descricao="Contrato de contragarantia do tomador: representantes legais, fiadores e assinatura."
      carregando={partes.isPending}
      erro={partes.error}
      acao={
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setHistoricoAberto(true)}
          >
            Historico de alteracoes
          </Button>
          {podeEditar && partesTravadas ? (
            <AcaoIndisponivel rotulo="Adicionar parte" motivo={motivoDaTrava} />
          ) : null}
          {podeEditar && !partesTravadas ? (
            <>
              <Button
                size="sm"
                onClick={() => setParte(vazioDaParte('representante_legal'))}
              >
                Adicionar representante
              </Button>
              <Button size="sm" onClick={() => setParte(vazioDaParte('fiador_pf'))}>
                Adicionar fiador
              </Button>
              <Button size="sm" onClick={() => setParte(vazioDaParte('fiador_pj'))}>
                Adicionar fiador PJ
              </Button>
            </>
          ) : null}
        </div>
      }
    >
      {tomador.ignore_ccg_signature ? (
        <Alert tone="warning" title="Assinatura de CCG ignorada">
          Este tomador esta com a assinatura de CCG dispensada. O efeito da dispensa na
          esteira esta pendente de regra.
        </Alert>
      ) : null}
      {tabela('representante_legal', 'Representantes legais do tomador')}
      {tabela('fiador_pf', 'Fiadores do tomador (Pessoas Fisicas)')}
      {tabela('fiador_pj', 'Fiadores do tomador (Pessoas Juridicas)')}
      <Bloco titulo="Assinatura da seguradora">
        <p className="text-sm text-ink-600">{PENDENCIA_ASSINATURA_SEGURADORA}</p>
      </Bloco>
      <div className="flex flex-wrap gap-4">
        <span className="inline-flex flex-col gap-1">
          <Link
            to="#documentos"
            className="text-xs font-semibold text-brand-700 underline"
            onClick={(event) => {
              event.preventDefault();
              window.dispatchEvent(
                new CustomEvent('tomador:secao', { detail: 'documentos' }),
              );
            }}
          >
            Upload Manual CCG
          </Link>
          <span className="max-w-xs text-2xs text-ink-500">
            A CCG assinada fora da plataforma entra em Documentos, no grupo CCG.
          </span>
        </span>
      </div>
      <ContratoDaCcg tomador={tomador} />

      <Modal
        open={historicoAberto}
        onClose={() => setHistoricoAberto(false)}
        title="Historico de alteracoes da CCG"
        size="lg"
      >
        <TabelaDeHistorico linhas={linhasDaCcg} vazio="Nenhuma alteracao na CCG" />
      </Modal>

      <Modal
        open={parte !== null}
        onClose={() => setParte(null)}
        title={
          parte?.role === 'fiador_pj'
            ? 'Adicionar fiador pessoa juridica'
            : parte?.role === 'fiador_pf'
              ? 'Adicionar fiador pessoa fisica'
              : 'Adicionar representante legal'
        }
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setParte(null)}>
              Cancelar
            </Button>
            <Button loading={incluir.isPending} onClick={() => incluir.mutate()}>
              Salvar
            </Button>
          </>
        }
      >
        {parte ? (
          <div className="flex flex-col gap-4">
            {incluir.error ? (
              <Alert tone="danger" title="Nao foi possivel incluir">
                {incluir.error.message}
              </Alert>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={
                  parte.role === 'fiador_pj'
                    ? 'CNPJ DO FIADOR PESSOA JURIDICA'
                    : parte.role === 'fiador_pf'
                      ? 'CPF DO(A) FIADOR PESSOA FISICA'
                      : 'CPF DO(A) REPRESENTANTE LEGAL'
                }
                required
                value={parte.document}
                error={fieldErrorOf(apiError, 'document')}
                onChange={(event) => set('document', event.target.value)}
              />
              <Field
                label={
                  parte.role === 'fiador_pj'
                    ? 'Razao social do fiador'
                    : 'Nome completo'
                }
                value={parte.name ?? ''}
                onChange={(event) => set('name', event.target.value)}
              />
              <Field
                label="E-mail pessoal"
                type="email"
                value={parte.email ?? ''}
                error={fieldErrorOf(apiError, 'email')}
                onChange={(event) => set('email', event.target.value)}
              />
              <Field
                label="Telefone da parte"
                value={parte.phone ?? ''}
                onChange={(event) => set('phone', event.target.value)}
              />
              {parte.role === 'fiador_pj' ? (
                <>
                  <Field
                    label="CPF DO REPRESENTANTE DO FIADOR PESSOA JURIDICA"
                    required
                    value={parte.representative_document ?? ''}
                    error={fieldErrorOf(apiError, 'representative_document')}
                    onChange={(event) =>
                      set('representative_document', event.target.value)
                    }
                  />
                  <Field
                    label="Nome do representante"
                    value={parte.representative_name ?? ''}
                    onChange={(event) => set('representative_name', event.target.value)}
                  />
                  <Field
                    label="E-mail do representante"
                    type="email"
                    value={parte.representative_email ?? ''}
                    error={fieldErrorOf(apiError, 'representative_email')}
                    onChange={(event) =>
                      set('representative_email', event.target.value)
                    }
                  />
                </>
              ) : (
                <>
                  <Field
                    label="Nacionalidade"
                    value={parte.nationality ?? ''}
                    onChange={(event) => set('nationality', event.target.value)}
                  />
                  <Field
                    label="Estado civil"
                    value={parte.marital_status ?? ''}
                    onChange={(event) => set('marital_status', event.target.value)}
                  />
                  <Field
                    label="Profissao"
                    value={parte.profession ?? ''}
                    onChange={(event) => set('profession', event.target.value)}
                  />
                  <Field
                    label="RG"
                    value={parte.identity_document ?? ''}
                    onChange={(event) => set('identity_document', event.target.value)}
                  />
                  <Field
                    label="Nome do conjuge"
                    value={parte.spouse_name ?? ''}
                    onChange={(event) => set('spouse_name', event.target.value)}
                  />
                  <Field
                    label="CPF do conjuge"
                    value={parte.spouse_document ?? ''}
                    error={fieldErrorOf(apiError, 'spouse_document')}
                    onChange={(event) => set('spouse_document', event.target.value)}
                  />
                </>
              )}
            </div>
            <AddressFields
              legend="Endereco da parte"
              value={parte.address ?? emptyAddress}
              onChange={(endereco) => set('address', endereco)}
            />
          </div>
        ) : null}
      </Modal>
    </Secao>
  );
}

// --- Apolices --------------------------------------------------------------------

interface LinhaDeApolice {
  chave: string;
  numero: string;
  endosso: string;
  status: ReactNode;
  emissao: string;
  vencimento: string;
  vigencia: string;
  apoliceId: string | null;
  documento: boolean;
  operacaoId: string;
  /** Segurado da apolice ou da operacao (TOM-167). Nulo quando nao tem. */
  segurado: { nome: string; documento: string } | null;
  /** A apolice da linha, para o dialogo Boletos; nula na operacao aguardando emissao. */
  apolice: Policy | null;
  /** Numero da cotacao, para o dialogo dos boletos da operacao aguardando emissao. */
  cotacao: string;
}

export function Apolices({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const toast = useToast();
  const carteira = useQuery({
    queryKey: ['policyholder', tomador.id, 'policies'],
    queryFn: ({ signal }) =>
      policies.list({ policyholder_id: tomador.id, limit: 100 }, signal),
  });
  // Como no OnPoint, a lista mostra tambem o que aguarda emissao: no SeguraCore,
  // a operacao na etapa de emissao.
  const aguardando = useQuery({
    queryKey: ['policyholder', tomador.id, 'awaiting-issuance'],
    queryFn: ({ signal }) =>
      operations.list(
        { policyholder_id: tomador.id, stage: 'em_emissao', limit: 100 },
        signal,
      ),
    enabled: can('operacoes.operacao.view'),
  });

  const baixar = useMutation({
    mutationFn: (apoliceId: string) => policies.document(apoliceId),
    onSuccess: (link) => {
      window.open(link.url, '_blank', 'noopener');
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  // "Visualizar boleto(s)" (TOM-174; P-BOL): os boletos que a seguradora anexou, da
  // apolice ou, na operacao que aguarda emissao, da cotacao.
  const [boletosDaApolice, setBoletosDaApolice] = useState<Policy | null>(null);
  const [boletosDaCotacao, setBoletosDaCotacao] = useState<{
    id: string;
    numero: string;
  } | null>(null);

  const linhas: LinhaDeApolice[] = [
    ...(aguardando.data?.items ?? []).map((operacao) => ({
      chave: `operacao-${operacao.id}`,
      numero: '',
      endosso: 'Nao',
      status: <Badge tone="brand">Aguardando emissao</Badge>,
      emissao: '',
      vencimento: formatCivilDate(operacao.coverage_end),
      vigencia:
        operacao.coverage_start && operacao.coverage_end
          ? `${formatCivilDate(operacao.coverage_start)} a ${formatCivilDate(operacao.coverage_end)}`
          : '',
      apoliceId: null,
      documento: false,
      operacaoId: operacao.id,
      segurado: operacao.insured
        ? { nome: operacao.insured.label, documento: operacao.insured.document }
        : null,
      apolice: null,
      cotacao: operacao.proposal_number ?? String(operacao.number),
    })),
    ...(carteira.data?.items ?? []).map((apolice) => ({
      chave: apolice.id,
      numero: apolice.external_number || String(apolice.number),
      endosso: (apolice.endorsement_count ?? 0) > 0 ? 'Sim' : 'Nao',
      status: <StatusBadge kind="apolice" value={apolice.status} />,
      emissao: formatDate(apolice.issued_at),
      vencimento: formatCivilDate(apolice.coverage_end),
      vigencia: `${formatCivilDate(apolice.coverage_start)} a ${formatCivilDate(apolice.coverage_end)}`,
      apoliceId: apolice.id,
      // O documento gerado do modelo vinculado (P-MIN-2) ou o arquivo ligado.
      documento: temDocumentoDaApolice(apolice),
      operacaoId: apolice.operation_id,
      // A apolice aponta para o segurado desde o P-SEG; o sublabel e o documento.
      segurado: apolice.insured
        ? { nome: apolice.insured.label, documento: apolice.insured.sublabel }
        : null,
      apolice,
      cotacao: '',
    })),
  ];

  return (
    <Secao
      titulo="Apolices"
      descricao="Apolices do tomador e operacoes aguardando emissao."
      carregando={carteira.isPending}
      erro={carteira.error}
    >
      <DataTable
        columns={[
          {
            key: 'numero',
            header: 'Numero',
            render: (l: LinhaDeApolice) => l.numero || '-',
          },
          {
            key: 'endosso',
            header: 'Contem endosso?',
            render: (l: LinhaDeApolice) => l.endosso,
          },
          {
            key: 'segurado',
            header: 'Segurado',
            // TOM-167: o segurado da apolice, com o CPF mascarado como o OnPoint
            // (InsuredDocumentMask) e o CNPJ formatado.
            render: (l: LinhaDeApolice) =>
              l.segurado ? (
                <div className="min-w-0">
                  <p className="truncate text-ink-900">{l.segurado.nome}</p>
                  {l.segurado.documento ? (
                    <p className="text-2xs tabular-nums text-ink-500">
                      {formatDocumentoDeTerceiro(l.segurado.documento)}
                    </p>
                  ) : null}
                </div>
              ) : (
                '-'
              ),
          },
          { key: 'status', header: 'Status', render: (l: LinhaDeApolice) => l.status },
          {
            key: 'emissao',
            header: 'Emissao',
            render: (l: LinhaDeApolice) => l.emissao || '-',
          },
          {
            key: 'vencimento',
            header: 'Vencimento',
            render: (l: LinhaDeApolice) => l.vencimento || '-',
          },
          {
            key: 'vigencia',
            header: 'Vigencia',
            render: (l: LinhaDeApolice) => l.vigencia || '-',
          },
          {
            key: 'acoes',
            header: '',
            render: (l: LinhaDeApolice) => (
              <div className="flex flex-col gap-1">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!l.apoliceId || !l.documento}
                  loading={baixar.isPending && baixar.variables === l.apoliceId}
                  title={
                    l.apoliceId && l.documento
                      ? undefined
                      : 'A apolice ainda nao tem documento: sem modelo vinculado na emissao, ou sem arquivo ligado a ela.'
                  }
                  onClick={() => (l.apoliceId ? baixar.mutate(l.apoliceId) : undefined)}
                >
                  Baixar apolice
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    l.apolice
                      ? setBoletosDaApolice(l.apolice)
                      : setBoletosDaCotacao({ id: l.operacaoId, numero: l.cotacao })
                  }
                >
                  Visualizar boleto(s)
                </Button>
                <Link
                  className="text-2xs text-brand-700 underline"
                  to={`/operacoes/${l.operacaoId}`}
                >
                  Abrir operacao
                </Link>
              </div>
            ),
          },
        ]}
        items={linhas}
        keyOf={(l) => l.chave}
        empty={<EmptyState icon="file" title="Nenhuma apolice para este tomador" />}
      />
      <p className="text-2xs text-ink-500">
        Baixar apolice: disponivel quando a apolice tem o documento gerado do modelo
        vinculado ou um arquivo ligado a ela. Visualizar boleto(s): os boletos que a
        seguradora anexou as parcelas, da apolice ou da cotacao que aguarda emissao; sem
        boleto anexado, o download fica desligado.
      </p>
      <BilletsDialog
        policy={boletosDaApolice}
        open={boletosDaApolice !== null}
        onClose={() => setBoletosDaApolice(null)}
      />
      {boletosDaCotacao ? (
        <QuoteBilletsDialog
          operationId={boletosDaCotacao.id}
          numero={boletosDaCotacao.numero}
          open
          onClose={() => setBoletosDaCotacao(null)}
        />
      ) : null}
    </Secao>
  );
}

// --- Socios / Representantes --------------------------------------------------------

export function Socios({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const toast = useToast();
  const invalidar = useInvalidar(tomador.id);
  const podeEditar = can('tomadores.ficha.update');
  const [novo, setNovo] = useState<{ role: PartnerRole } | null>(null);
  const [nome, setNome] = useState('');
  const [documento, setDocumento] = useState('');
  const [tipo, setTipo] = useState<'cpf' | 'cnpj'>('cpf');
  const [participacao, setParticipacao] = useState('');
  const [pep, setPep] = useState(false);
  const [motivoPep, setMotivoPep] = useState('');
  const [cargo, setCargo] = useState('');
  const [privado, setPrivado] = useState('');

  const socios = useQuery({
    queryKey: ['policyholder', tomador.id, 'partners'],
    queryFn: ({ signal }) => policyholderFile.partners(tomador.id, signal),
  });
  const incluir = useMutation({
    mutationFn: () =>
      policyholderFile.addPartner(tomador.id, {
        role: novo?.role ?? 'socio',
        name: nome,
        document: documento,
        document_kind: tipo,
        role_description: cargo,
        participation_bps: participacao.trim() === '' ? null : parseRate(participacao),
        is_pep: pep,
        // ESC-1015: o motivo so vai com o PEP marcado.
        pep_reason: pep ? motivoPep.trim() : '',
        is_private: privado === '' ? null : privado === 'sim',
      }),
    onSuccess: () => {
      toast.success('Pessoa incluida.');
      setPrivado('');
      setNovo(null);
      setNome('');
      setDocumento('');
      setParticipacao('');
      setPep(false);
      setMotivoPep('');
      setCargo('');
      invalidar();
    },
  });
  const remover = useMutation({
    mutationFn: (socioId: string) =>
      policyholderFile.removePartner(tomador.id, socioId),
    onSuccess: () => {
      toast.success('Pessoa removida.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const apiError = erroDaApi(incluir.error);

  function tabela(papel: PartnerRole, titulo: string, comParticipacao: boolean) {
    const itens = (socios.data ?? []).filter((item) => item.role === papel);
    return (
      <Bloco
        titulo={titulo}
        acao={
          podeEditar ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setNovo({ role: papel })}
            >
              Adicionar
            </Button>
          ) : null
        }
      >
        <DataTable
          columns={[
            { key: 'nome', header: 'Nome', render: (p: Partner) => p.name },
            {
              key: 'documento',
              header: 'CPF/CNPJ',
              render: (p: Partner) => formatCpfCnpj(p.document),
            },
            ...(comParticipacao
              ? [
                  {
                    key: 'participacao',
                    header: 'Participacao',
                    render: (p: Partner) => percentual(p.participation_bps),
                  },
                ]
              : []),
            {
              key: 'pep',
              header: 'PEP',
              // ESC-1015: o motivo de ser PEP, quando informado.
              render: (p: Partner) =>
                p.is_pep ? (
                  <div className="min-w-0">
                    <p>Sim</p>
                    {p.pep_reason ? (
                      <p className="text-2xs text-ink-500">{p.pep_reason}</p>
                    ) : null}
                  </div>
                ) : (
                  'Nao'
                ),
            },
            {
              key: 'privado',
              header: 'Direito privado',
              render: (p: Partner) =>
                p.is_private === true ? 'Sim' : p.is_private === false ? 'Nao' : '-',
            },
            {
              key: 'invalido',
              header: 'Documento',
              render: (p: Partner) =>
                p.document_invalid ? 'Invalido (carga)' : 'Conferido',
            },
            {
              key: 'acoes',
              header: '',
              render: (p: Partner) =>
                podeEditar ? (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => remover.mutate(p.id)}
                  >
                    Remover
                  </Button>
                ) : null,
            },
          ]}
          items={itens}
          keyOf={(p) => p.id}
          empty={<p className="text-sm text-ink-400">Nenhuma pessoa cadastrada.</p>}
        />
      </Bloco>
    );
  }

  return (
    <Secao
      titulo="Socios / Representantes"
      descricao="Quadro societario do tomador. Sem consulta ao quadro no bureau, as pessoas sao registradas a mao."
      carregando={socios.isPending}
      erro={socios.error}
    >
      {tabela('socio', 'Socios', true)}
      {tabela('administrador', 'Administradores', false)}
      {tabela('representante', 'Representantes', false)}
      <Modal
        open={novo !== null}
        onClose={() => setNovo(null)}
        title="Adicionar pessoa ao quadro"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setNovo(null)}>
              Cancelar
            </Button>
            <Button
              loading={incluir.isPending}
              disabled={rateInputError(participacao) !== undefined}
              onClick={() => incluir.mutate()}
            >
              Salvar
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {incluir.error ? (
            <Alert tone="danger" title="Nao foi possivel incluir">
              {incluir.error.message}
            </Alert>
          ) : null}
          <Field
            label="Nome"
            required
            value={nome}
            error={fieldErrorOf(apiError, 'name')}
            onChange={(event) => setNome(event.target.value)}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Tipo de documento"
              options={[
                { value: 'cpf', label: 'CPF' },
                { value: 'cnpj', label: 'CNPJ' },
              ]}
              value={tipo}
              onChange={(event) =>
                setTipo(event.target.value === 'cnpj' ? 'cnpj' : 'cpf')
              }
            />
            <Field
              label="Documento"
              required
              value={documento}
              error={fieldErrorOf(apiError, 'document')}
              onChange={(event) => setDocumento(event.target.value)}
            />
          </div>
          <Field
            label="Cargo ou qualificacao"
            value={cargo}
            onChange={(event) => setCargo(event.target.value)}
          />
          {novo?.role === 'socio' ? (
            <Field
              label="Participacao (%)"
              inputMode="decimal"
              value={participacao}
              error={
                rateInputError(participacao) ??
                fieldErrorOf(apiError, 'participation_bps')
              }
              onChange={(event) => setParticipacao(event.target.value)}
            />
          ) : null}
          <label className="flex items-center gap-2 text-xs font-semibold text-ink-700">
            <input
              type="checkbox"
              className="size-4"
              checked={pep}
              onChange={(event) => setPep(event.target.checked)}
            />
            Pessoa politicamente exposta (PEP)
          </label>
          {pep ? (
            <Textarea
              label="Motivo do PEP"
              rows={2}
              maxLength={2000}
              value={motivoPep}
              hint="Por que a pessoa e PEP. Nao decide nada: o efeito do PEP e regra pendente (CAD-015)."
              error={fieldErrorOf(apiError, 'pep_reason')}
              onChange={(event) => setMotivoPep(event.target.value)}
            />
          ) : null}
          <Select
            label="Direito privado"
            placeholder="Nao informado"
            options={[
              { value: 'sim', label: 'Sim' },
              { value: 'nao', label: 'Nao' },
            ]}
            value={privado}
            hint="Significado a confirmar. Nao decide nada."
            onChange={(event) => setPrivado(event.target.value)}
          />
        </div>
      </Modal>
    </Secao>
  );
}

// --- Documentos --------------------------------------------------------------------

/**
 * Documentos do tomador, no painel comum de documentos: "Documentos disponiveis",
 * "Upload de documento" (nomear e enviar de uma vez, no grupo Tomador) e "Documentos
 * excluidos", como o OnPoint.
 */
export function Documentos({ tomador }: { tomador: Policyholder }) {
  return (
    <DocumentsPanel
      ownerType="tomador"
      ownerId={tomador.id}
      title="Documentos disponiveis"
      group="Tomador"
      allowDelete
    />
  );
}

// --- Analises ----------------------------------------------------------------------

export function Analises({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const podeVer = can('credito.analise.view');
  const analises = useQuery({
    queryKey: ['policyholder', tomador.id, 'credit-analyses'],
    queryFn: ({ signal }) => policyholderFile.creditAnalyses(tomador.id, signal),
    enabled: podeVer,
  });

  if (!podeVer) {
    return (
      <Secao titulo="Analises">
        <Alert tone="info" title="Atencao!">
          {PENDENCIA_ANALISES_CORRETORA}
        </Alert>
      </Secao>
    );
  }

  return (
    <Secao
      titulo="Analises"
      descricao="Analises de credito das operacoes do tomador."
      carregando={analises.isPending}
      erro={analises.error}
    >
      <DataTable
        columns={[
          {
            key: 'operacao',
            header: 'Operacao',
            render: (a) => (
              <Link
                className="text-brand-700 underline"
                to={`/operacoes/${a.operation.id}`}
              >
                Operacao {a.operation.number}
              </Link>
            ),
          },
          {
            key: 'etapa',
            header: 'Etapa',
            render: (a) => statusLabel('operacao', a.operation.stage),
          },
          {
            key: 'situacao',
            header: 'Situacao',
            render: (a) => <StatusBadge kind="credito" value={a.status} />,
          },
          {
            key: 'tradicional',
            header: 'Limite tradicional',
            align: 'right',
            render: (a) => formatMoney(a.traditional.limit),
          },
          {
            key: 'judicial',
            header: 'Limite judicial e financeiro',
            align: 'right',
            render: (a) => formatMoney(a.judicial.limit),
          },
          {
            key: 'validade',
            header: 'Validade',
            render: (a) => formatCivilDate(a.valid_until) || '-',
          },
          {
            key: 'analista',
            header: 'Analista',
            render: (a) => a.analyst_label || '-',
          },
          {
            key: 'decisao',
            header: 'Decidido em',
            render: (a) => formatDateTime(a.decided_at) || '-',
          },
        ]}
        items={analises.data ?? []}
        keyOf={(a) => a.id}
        empty={
          <EmptyState
            icon="coins"
            title="Ops! Nenhuma analise encontrada para este tomador."
          />
        }
      />
    </Secao>
  );
}

// --- Historicos ----------------------------------------------------------------------

/**
 * A aba comum de historico (P-FUN-2b), pela rota generica: a mesma trilha da rota da
 * ficha, com as mesmas permissoes, mais a origem (IP e navegador) de cada alteracao.
 */
export function HistoricoDeAlteracoes({ tomador }: { tomador: Policyholder }) {
  return (
    <HistoryTab
      entityType="policyholder"
      entityId={tomador.id}
      title="Historico de Alteracoes"
      description="Toda mudanca na ficha do tomador, com o antes e o depois."
      emptyText="Nenhuma alteracao registrada"
      actionLabel={rotuloDaAcao}
    />
  );
}

export function HistoricoDeConsultas({ tomador }: { tomador: Policyholder }) {
  const [aberta, setAberta] = useState<CreditConsultation | null>(null);
  const consultas = useQuery({
    queryKey: ['policyholder', tomador.id, 'consultations'],
    queryFn: ({ signal }) => policyholderFile.consultations(tomador.id, signal),
  });
  return (
    <Secao
      titulo="Historico de Consultas"
      descricao="Consultas ao bureau de credito feitas para o tomador."
      carregando={consultas.isPending}
      erro={consultas.error}
    >
      <DataTable
        columns={[
          {
            key: 'data',
            header: 'Consultado em',
            render: (c) => formatDateTime(c.consulted_at),
          },
          { key: 'tipo', header: 'Consulta', render: (c) => tipoDaConsulta(c) },
          {
            key: 'provedor',
            header: 'Provedor',
            render: (c) => nomeDoProvedor(c.provider),
          },
          { key: 'situacao', header: 'Situacao', render: (c) => c.status },
          {
            key: 'simulada',
            header: 'Simulada',
            render: (c) =>
              c.simulated ? <Badge tone="warning">SIMULADO</Badge> : 'Nao',
          },
          {
            key: 'validade',
            header: 'Valida ate',
            render: (c) => formatDateTime(c.valid_until),
          },
          {
            key: 'quem',
            header: 'Solicitado por',
            render: (c) => c.requested_label || '-',
          },
          {
            key: 'dados',
            header: 'Dados',
            render: (c) => (
              <Button
                variant="secondary"
                size="sm"
                disabled={!c.values?.length}
                onClick={() => setAberta(c)}
              >
                Ver dados
              </Button>
            ),
          },
        ]}
        items={consultas.data ?? []}
        keyOf={(c) => c.id}
        empty={<EmptyState icon="refresh" title="Nenhuma consulta realizada" />}
      />
      <DadosDaConsultaModal consulta={aberta} onClose={() => setAberta(null)} />
    </Secao>
  );
}
